import * as ko from "knockout";
import {moment} from 'moment';
import PromiseRequest from "../../../req/PromiseRequest";
import {ButtonManager} from "../../../util/ButtonManager";
import {ObjectTypeId} from "../../../constants";
import {ErrorObject} from "../../../error/ErrorObject";
import {
    IAuthenticationType,
    ICompany,
    ICompanySyncstep,
    ICompanyLogin,
    IMessageData,
    IOrganization,
    IRouteDefinitionParams,
    ISyncField
} from "../../../index";
import {ICompanyError, ICompanyMutable} from "./index";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {convertToServerString} from "../../../util/datetime";
import {getCancelButton, getDiscardButton, getSubmitButton, setRouteHash} from "../../../constants/RouteDefinitions";
import {
    createErrorMessage,
    getObjectType,
    isBit,
    isImportFormat,
    TImportFormatKey,
    validateEmail,
    validateObjectGuid
} from "../../../util/misc";
import {global, lm} from "../../../appContext";
import {nsCustomerGeneral} from "./fn";
import {nsRoute} from "../../../util/route";
import {REQUEST_MESSAGE} from "../../../constants/Language";
import {_} from 'lodash';
import {nsCustomer} from "../nsCustomer"
import {IComboboxV2Options} from "../../combobox/v2";
import {storeObservable} from "../../../util/sessionstore";
import {nsTooltip} from "../../../util/nsTooltip";
import {AuthenticationTypeFlag} from "../../../constants/AuthenticationTypeFlag";
import {SyncstepId} from "../../../constants/Syncstep";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import urlEncodeCustomerKeys = nsCustomerGeneral.urlEncodeCustomerKeys;
import mutableHtml from './mutable.html?raw';

export let template: string = mutableHtml;

export abstract class viewModel extends ParsedRouteDataDecorator {
    private readonly USERNAME_GENERATION_RULE_ID_FOR_EXPRESSION = nsCustomerGeneral.USERNAME_GENERATION_RULE_ID_FOR_EXPRESSION
    private readonly ObjectTypeId = ObjectTypeId;

    // override these in descendant classes
    protected abstract readonly msg_success: string
    protected abstract readonly msg_submitting: string
    protected abstract req_submit: PromiseRequest;

    // default form data
    protected data!: ICompany;
    protected d!: ICompanyMutable;
    protected errObj!: ICompanyError;

    private warnAboutSyncHalted: KnockoutObservable<boolean | null> = ko.observable(null);
    private purgeDeactivatedAccounts: KnockoutObservable<boolean | null> = ko.observable(null);
    private readonly logLevel = nsCustomerGeneral.logLevel;
    private readonly req_searchruleset = global.getPromiseRequest('searchruleset');
    private readonly req_getauthenticationtypes = global.getPromiseRequest('getauthenticationtypes');
    protected readonly kosub = new KOSubscriptionTracker();
    protected readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    protected readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    protected readonly ignoreImportfilterUntil = ko.observable(null);
    private readonly pageButtons: ButtonManager = new ButtonManager();

    private readonly AvailableSoftMergeAttributeNames: ISyncField['name'][] = [
        'Email',
        'EmployeeNumber',
        'Mail',
        'Mobile',
        'PrivatePhone',
        'Socialsecuritynumber',
        'SourceId',
        'UserId',
        'UserName',
        'WebUserId',
    ]
    private allAuthenticationTypes!: IAuthenticationType[];
    private readonly isCreateCustomer: boolean;
    /** Bumped after discard so create-customer SSO draft UI can drop stale wizard/list state. */
    readonly ssoDraftRevision = ko.observable(0);
    private selectedAuthenticationTypes: KnockoutObservableArray<IAuthenticationType> = ko.observableArray<IAuthenticationType>([]);
    private usernamesharingcompany_arr: KnockoutObservableArray<IOrganization['organizationid']> = ko.observableArray<IOrganization['organizationid']>([])
    private hasImportHR2Syncstep = ko.pureComputed((): boolean =>
        (this.d?.syncsteps?.() ?? []).some((step: ICompanySyncstep) => step.syncstep.id === SyncstepId.ImportHR2)
    )

    constructor(params: IRouteDefinitionParams) {
        super(params);
        this.isCreateCustomer = this.__routeId === "createcustomer";
        const cancelButton = getCancelButton();
        if (cancelButton) {
            this.pageButtons.add(cancelButton);
        }
        this.pageButtons.add(getDiscardButton({
            onClick: this.onDiscardChangesClick.bind(this),
            enabled: ko.pureComputed(() => this.isReady() && this.isFormChanged()),
        }))
        this.pageButtons.add(getSubmitButton({
            attr: {type: 'submit'},
            onClick: this.onSubmit.bind(this),
            enabled: ko.pureComputed((): boolean => {
                if (!this.isReady()) {
                    return false;
                }
                return this.isFormValid() && this.isFormChanged();
            }),
        }))
        this.kosub.subscribe(this.warnAboutSyncHalted, bool => this.updateSwitchAndObservable(bool ?? false, this.d.daystosyncstopwarning, 30))
        this.kosub.subscribe(this.purgeDeactivatedAccounts, bool => this.updateSwitchAndObservable(bool ?? false, this.d.purgedeactivatedaccountsafterdays, 1000))
    }

    protected init() {
        const allKeys = Object.keys(nsCustomerGeneral.DefaultValues);
        if (!this.d) {
            this.d = {} as ICompanyMutable;
            _.forEach(allKeys, (key: keyof ICompany) => {
                this.d[key] = ko.observable(this.data[key]);
            });
        }

        if (!this.errObj) {
            this.errObj = {};
            _.forEach(allKeys, (key: keyof ICompany) => {
                const obj = new ErrorObject({name: key});
                this.errObj[key] = obj;

                switch (key) {
                    case 'name':
                    case 'id':
                    case 'ssotenantid':
                    case 'vanitynames':
                        this.__tabManager?.getTab('customerinfo')?.errorManager.addErrorObject(obj);
                        break;
                    case 'ssogoogleidpid':
                    case 'grandidservicekey':
                    case 'grandidapikey':
                        this.__tabManager?.getTab('customerinfo')?.errorManager.addErrorObject(obj);
                        break;
                    case 'importformathi':
                    case 'importformathr2':
                        this.__tabManager?.getTab('customerimport')?.errorManager.addErrorObject(obj);
                        break;
                    case 'smssenders':
                        this.__tabManager?.getTab('customercommunication')?.errorManager.addErrorObject(obj);
                        break
                    case 'eadmapiusername':
                    case 'eadmapipassword':
                        this.__tabManager?.getTab('customerapiaccess')?.errorManager.addErrorObject(obj);
                        break
                }
            });
        }

        _.forEach(allKeys, (key: keyof ICompany) => {
            const v = this.data[key] ?? nsCustomerGeneral.DefaultValues[key];
            const obs = this.d[key];
            if (!obs) {
                return;
            }
            switch (key) {
                case 'smssenders':
                case 'vanitynames':
                    obs((v as string).split(',').join('\n'));
                    break;
                case 'usernamesharingcompany': {
                    if (!v) {
                        this.usernamesharingcompany_arr([])
                    } else {
                        const sorted = _(v.split(','))
                            .map((s: string) => global.login.getOrganizationById(s))
                            .sortBy((o: IOrganization | undefined) => o?.organizationame?.toLowerCase())
                            .map((o: IOrganization | undefined) => o?.organizationid)
                            .value()
                        this.usernamesharingcompany_arr(sorted)
                    }
                    break
                }
                default:
                    obs(v);
            }

            const errObj = this.errObj[key];
            if (!errObj) {
                return;
            }
            errObj.init()

            switch (key) {
                case 'id':
                    errObj.addValidator({
                        fn: () => !!obs(),
                        message: 'Ugyldig organisasjonsnummer',
                    })
                    break
                case 'smssenders':
                    errObj.addValidator({
                        fn: () => obs() == '' || obs()
                            .split("\n")
                            .every((sender: string) => Boolean(sender)),
                        message: '((invalid)) ((sender|ac))',
                    })
                    errObj.addValidator({
                        fn: () => {
                            const arr = obs().split("\n");
                            const uniq = new Set(arr)
                            return arr.length == uniq.size;
                        },
                        message: 'list contains duplicates',
                    })
                    break
                case 'vanitynames':
                    errObj.addValidator({
                        fn: () => obs() == '' || obs()
                            .split("\n")
                            .every((sender: string) => Boolean(sender)),
                        message: '((invalid)) ((name|ac))',
                    })
                    errObj.addValidator({
                        fn: () => {
                            const arr = obs().split("\n");
                            const uniq = new Set(arr)
                            return arr.length == uniq.size;
                        },
                        message: 'list contains duplicates',
                    })
                    break
                case 'defaultsmtpsender':
                    errObj.addValidator({
                        fn: () => validateEmail(obs()),
                        message: 'Invalid email',
                    })
                    break
                case 'authenticationtype':
                case 'eadmapipassword':
                case 'eadmapiusername':
                case 'importformathi':
                case 'name':
                case 'passwordgenerationrule':
                case 'status':
                case 'usernamegenerationrule':
                    // value must simply be set
                    errObj.addValidator({
                        fn: () => !!obs(),
                        message: 'invalid value',
                    })
                    break
                case 'importformathr2':
                    errObj.addValidator({
                        fn: () => !!obs(),
                        message: 'invalid value',
                        prerequisite: () => this.hasImportHR2Syncstep(),
                    })
                    break
                case 'ssotenantid':
                    errObj.addValidator({
                        fn: () => validateObjectGuid(obs()),
                        message: 'Invalid s|s:((GUID))',
                        prerequisite: () => this.isCreateCustomer && this.isAuthenticationTypeAADFederation(),
                    })
                    break;
                case 'ssogoogleidpid':
                    errObj.addValidator({
                        fn: () => !!obs(),
                        message: 'Invalid value',
                        prerequisite: () => this.isCreateCustomer && this.isAuthenticationTypeGoogle(),
                    })
                    break;
                case 'grandidapikey':
                case 'grandidservicekey':
                    errObj.addValidator({
                        fn: () => !!obs(),
                        message: 'Invalid value',
                        prerequisite: () => this.isCreateCustomer && this.isAuthenticationTypeGrandIdBankIdSe(),
                    })
                    break;
            }
        });

        this.warnAboutSyncHalted(!!this.d.daystosyncstopwarning());
        this.purgeDeactivatedAccounts(!!this.d.purgedeactivatedaccountsafterdays());

        // make sure observable holds the _exact_ object from getauthenticationtypes (so that isFormChanged() doesn't respond to changing back to original authentication type)
        const authenticationType = this.isCreateCustomer
            ? this.allAuthenticationTypes.find(o => o.flag === AuthenticationTypeFlag.ID_PORTEN)
            : this.allAuthenticationTypes.find(o => o.id == this.data.authenticationtype?.id);
        if (authenticationType) {
            this.data.authenticationtype = authenticationType;
        }
        this.d.authenticationtype(authenticationType)

        this.d.authenticationtypes(0)
        const selectedAuthenticationTypes: IAuthenticationType[] = _(this.allAuthenticationTypes)
            .filter((o: IAuthenticationType) => isBit(o.flag, this.data.authenticationtypes))
            .value()
        this.selectedAuthenticationTypes(selectedAuthenticationTypes)

    }

    protected initRequest(): Promise<any> {
        this.isReady(false);
        this.messageData({message: REQUEST_MESSAGE});
        return Promise.resolve()
            .then(() => this.req_searchruleset.request())
            .then(() => this.req_getauthenticationtypes
                .request()
                .then((data: IAuthenticationType[]) => {
                    this.allAuthenticationTypes = _.sortBy(data, (o: IAuthenticationType) => o.name.toLowerCase())
                }))
            .catch(reason => this.messageData(createErrorMessage(String(reason))))
    }

    readSsoDraftCompany = (): ICompany => {
        const primary = this.d.authenticationtype();
        const flags = this.selectedAuthenticationTypes().reduce(
            (mask, type) => mask | (type?.flag ?? 0),
            primary?.flag ?? 0,
        );
        return {
            authenticationtype: primary,
            authenticationtypes: flags,
            ssotenantid: this.d.ssotenantid() ?? "",
            ssogoogleidpid: this.d.ssogoogleidpid() ?? "",
            grandidapikey: this.d.grandidapikey() ?? "",
            grandidservicekey: this.d.grandidservicekey() ?? "",
        } as ICompany;
    };

    writeSsoDraftCompany = (company: ICompany): void => {
        const match = this.allAuthenticationTypes.find(item => item.id == company.authenticationtype?.id);
        if (match) {
            this.d.authenticationtype(match);
        }
        this.d.ssotenantid(company.ssotenantid ?? "");
        this.d.ssogoogleidpid(company.ssogoogleidpid ?? "");
        this.d.grandidapikey(company.grandidapikey ?? "");
        this.d.grandidservicekey(company.grandidservicekey ?? "");
        const primaryId = (match ?? this.d.authenticationtype())?.id;
        this.selectedAuthenticationTypes(
            this.allAuthenticationTypes.filter(item => item.id != primaryId && isBit(item.flag, company.authenticationtypes)),
        );
    };

    private updateSwitchAndObservable(bool: boolean, obs: KnockoutObservable<number>, max: number) {
        if (bool) {
            let num = obs();
            if (isNaN(num) || num < 1 || num > max) {
                num = 1;
            }
            obs(num);
        }
    }

    isFormValid = ko.pureComputed(() => {
        return (Object.keys(this.errObj) as (keyof ICompanyError)[])
            .map((key) => this.errObj[key])
            .filter((obj): obj is ErrorObject => !!obj)
            .reduce((bool: boolean, obj: ErrorObject) => bool && obj.validate(), true)
    });

    isFormChanged = ko.pureComputed((): boolean => {
        const submitData = this.getSubmitData();
        const keys = Object.keys(this.data) as (keyof ICompany)[];
        const ret = !keys.every((k): boolean => {
            const defaultVal = this.data[k];
            const submitVal = submitData[k];
            const isEqual = _.isEqual(defaultVal, submitVal);
            const isNoValue = submitVal === null && defaultVal === undefined;
            return isEqual || isNoValue;
        });
        return ret;
    });

    onDiscardChangesClick() {
        this.initRequest().then(() => {
            if (this.isCreateCustomer) {
                this.ssoDraftRevision(this.ssoDraftRevision() + 1);
            }
        });
        global.snackbarData({message: `all changes discarded`});
    }

    onSubmit() {
        this.isReady(false);
        this.messageData({message: this.msg_submitting});
        const data: ICompany = this.getSubmitData(true);

        const json = JSON.stringify(data);

        this.req_submit.abort();
        this.req_submit
            .request({jcompany: json})
            .then((data: ICompany[]) => {
                this.messageData(null);
                global.snackbarData({message: this.msg_success});
                global.cacheManager.removeCache("getcompany");
                global.cacheManager.removeCache("getcompanylogin");
                global.login.relogin(() => {
                    const company = data[0];
                    if (!company) {
                        return;
                    }
                    global.login.setSelectedOrganizationById(company.id);
                    setRouteHash('customerconfig');
                });
            })
            .catch((reason: string) => {
                this.messageData(createErrorMessage(String(reason)))
                this.isReady(true);
                document.body.scrollIntoView();
            });
    }


    protected getSubmitData(encode?: boolean): ICompany {
        const ret: any = {};
        _.forEach(this.d, (obs: ICompanyMutable[keyof ICompanyMutable], key: keyof ICompanyMutable) => {
            ret[key] = this.getSubmitValue(key, encode);
        });
        return ret;
    }

    noteAuthenticationSaved = (company: ICompany): void => {
        this.data.authenticationtype = company.authenticationtype;
        this.data.authenticationtypes = company.authenticationtypes;
        this.data.ssotenantid = company.ssotenantid;
        this.data.ssogoogleidpid = company.ssogoogleidpid;
        this.data.grandidapikey = company.grandidapikey;
        this.data.grandidservicekey = company.grandidservicekey;
        if (!this.d) {
            return;
        }
        const match = this.allAuthenticationTypes?.find(item => item.id == company.authenticationtype?.id);
        if (match) {
            this.d.authenticationtype(match);
        }
        this.d.ssotenantid(company.ssotenantid);
        this.d.ssogoogleidpid(company.ssogoogleidpid);
        this.d.grandidapikey(company.grandidapikey);
        this.d.grandidservicekey(company.grandidservicekey);
        if (this.allAuthenticationTypes) {
            this.selectedAuthenticationTypes(this.allAuthenticationTypes.filter(item => isBit(item.flag, company.authenticationtypes)));
        }
    }

    private getSubmitValue(key: keyof ICompanyMutable, encode?: boolean): any {
        if (!this.isCreateCustomer && (
            key === "authenticationtype"
            || key === "authenticationtypes"
            || key === "ssotenantid"
            || key === "ssogoogleidpid"
            || key === "grandidapikey"
            || key === "grandidservicekey"
        )) {
            return this.data[key];
        }
        const obs = this.d[key];
        if (!obs) {
            return null;
        }
        switch (key) {
            case 'agressoorguntitype':
                return this.is_importformathr('Agresso')
                    ? obs()
                    : '';
            case 'authenticationtypes': {
                const authenticationTypes = _.clone(this.selectedAuthenticationTypes())
                authenticationTypes.push(this.d.authenticationtype())
                return _(authenticationTypes)
                    .map((o: IAuthenticationType) => o.flag)
                    .uniq()
                    .reduce((flags: number, flag: number) => flags + flag, 0)
            }
            case "defaultexpirypermissions":
                return Number(obs());
            case 'getaccountsdaysbefore':
                return this.is_importformathr('Agresso', 'SD Worx', 38, 27)
                    ? obs()
                    : 0;
            case 'getdepartmentfromorgunit':
                return !!this.d.agressoorguntitype();
            case 'daystosyncstopwarning':
                return this.warnAboutSyncHalted()
                    ? obs()
                    : 0;
            case 'ssotenantid': {
                const isAAD = this.isAuthenticationTypeAADFederation()
                if (isAAD) {
                    return obs()
                } else {
                    return ''
                }
            }
            case 'ssogoogleidpid': {
                const isGoogle = this.isAuthenticationTypeGoogle()
                if (isGoogle) {
                    return obs()
                } else {
                    return ''
                }
            }

            case 'syncstopwarningdeactivated':
                if (this.warnAboutSyncHalted()) {
                    return '';
                } else {
                    return obs()
                        // don't change the date-time if it's already set
                        ? obs()
                        // otherwise use now
                        : convertToServerString(moment());
                }
            case 'syncstopwarningdeactivatedby':
                if (this.warnAboutSyncHalted()) {
                    return 0;
                } else {
                    return obs()
                        // don't change the user's id if it's already set (non-zero)
                        ? obs()
                        // otherwise use current user's id
                        : global.login.user()?.id;
                }
            case 'getdepartmentfromaccountingdimension':
                return this.is_importformathr('Visma')
                    ? obs()
                    : false;
            case 'positionfilter':
                return this.d.positionfilter() ?? null;
            case 'smssenders':
            case 'vanitynames':
                return (obs() ?? '').split("\n").join(",");
            case 'usernamesharingcompany':
                return this.usernamesharingcompany_arr().join(',')
            case 'vismaincludecharttopnode':
                return this.d.importformathi()?.id == 4 ? obs() : false;
            case 'purgedeactivatedaccountsafterdays':
                return this.purgeDeactivatedAccounts()
                    ? Number(obs())
                    : 0;
            case 'eadmapipassword':
            case 'eadmapiusername':
                return encode ? encodeURIComponent(obs()) : obs();
            case 'syncsteps':
                return urlEncodeCustomerKeys(this.d.syncsteps())
            default:
                if (obs() === undefined) {
                    return null;
                } else {
                    return obs();
                }
        }
    }

    private getObjectType = getObjectType;

    private is_importformathr = (...key: TImportFormatKey[]) => {
        if (!this.d.importformathr()) {
            return false
        }
        return key.some(k => isImportFormat(k, this.d.importformathr()));
    }

    private isAuthenticationTypeAADFederation(): boolean {
        return this.isAuthenticationTypeSupported(AuthenticationTypeFlag.AAD_FEDERATION)
    }

    private isAuthenticationTypeGoogle(): boolean {
        return this.isAuthenticationTypeSupported(AuthenticationTypeFlag.GOOGLE)
    }

    private isAuthenticationTypeGrandIdBankIdSe(): boolean {
        return this.isAuthenticationTypeSupported(AuthenticationTypeFlag.GRAND_ID_BANKID_SE)
    }

    private isAuthenticationTypeSupported(authenticationTypeFlag: AuthenticationTypeFlag): boolean {
        const isPrimary = nsCustomer.isPrimaryAuthenticationType(this.d.authenticationtype(), authenticationTypeFlag)
        const isSecondary = nsCustomer.isSecondaryAuthenticationType(this.allAuthenticationTypes, this.getSubmitValue('authenticationtypes'), authenticationTypeFlag)
        return isPrimary || isSecondary
    }

    private getComboboxSelectOrganizationParams(): IComboboxV2Options<IOrganization> {
        return {
            prepareData: {
                map: arr => {
                    const companylogin_selected = storeObservable('selected.company.login')() as ICompanyLogin;
                    if (companylogin_selected?.vanityindex) {
                        // replace the name with the one from companylogin
                        const a = _.cloneDeep(arr)
                        const item = a.find((o: IOrganization) => o.organizationid == companylogin_selected.orgnr)
                        item.organizationame = companylogin_selected.name
                        return a
                    }
                    return arr
                },
            },
            inputAttrs: {placeholder: lm.t('Add subject|s:((organization|ac))'), title: lm.t('pick s|s:((organization|ac))')},
            option: {
                constrain: o => !this.usernamesharingcompany_arr().includes(o.organizationid),
                label: o => o?.organizationame ?? '',
                tooltip: o => o ? lm.t(nsTooltip.getTitle_organization(o) ?? '') : '',
            },
            selectionMode: 'pick',
            onSelect: o => {
                if (!o) {
                    return
                }
                this.usernamesharingcompany_arr.push(o.organizationid)
            },
            data: global.login.organizations(),
        }
    }

    override dispose() {
        super.dispose();
        this.req_submit.abort();
        this.req_getauthenticationtypes.abort();
    }

}
